import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const TeamAnalyticsPanel = ({ teamId, teamName, onExportClick }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports/team-report', {
        params: { teamId, from: dateFrom, to: dateTo }
      });
      setReportData(response.data.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  }, [teamId, dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = (format) => {
    onExportClick(format, dateFrom, dateTo);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <p>Failed to load analytics</p>
      </div>
    );
  }

  const stats = reportData.statistics;
  const complianceData = stats.memberCompliance || [];

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4 flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-end">
        <div className="flex-1 min-w-0">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">From Date</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">To Date</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <button
          onClick={fetchReport}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          Refresh
        </button>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => handleExport('csv')}
            className="flex-1 sm:flex-none px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs sm:text-sm font-medium"
            title="Export as CSV"
          >
            📥 CSV
          </button>
          <button
            onClick={() => handleExport('html')}
            className="flex-1 sm:flex-none px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-xs sm:text-sm font-medium"
            title="Export as HTML/PDF"
          >
            📥 HTML
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          title="Total Expected"
          value={stats.totalExpectedReports}
          icon="📊"
          color="blue"
        />
        <MetricCard
          title="Submitted"
          value={stats.submittedReports}
          icon="✅"
          color="green"
        />
        <MetricCard
          title="Draft"
          value={stats.draftReports}
          icon="✏️"
          color="yellow"
        />
        <MetricCard
          title="Rate"
          value={`${stats.submissionRate}%`}
          icon="📈"
          color="purple"
        />
      </div>

      {/* Member Compliance */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-4 text-gray-900">Member Compliance</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left px-2 sm:px-4 py-2 sm:py-3 font-semibold text-gray-700">Member</th>
                <th className="text-center px-2 sm:px-4 py-2 sm:py-3 font-semibold text-gray-700 text-xs">Sub</th>
                <th className="text-center px-2 sm:px-4 py-2 sm:py-3 font-semibold text-gray-700 text-xs">Draft</th>
                <th className="text-center px-2 sm:px-4 py-2 sm:py-3 font-semibold text-gray-700 text-xs">Miss</th>
                <th className="text-right px-2 sm:px-4 py-2 sm:py-3 font-semibold text-gray-700 text-xs sm:text-sm">Rate</th>
              </tr>
            </thead>
            <tbody>
              {complianceData.map((member, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-2 sm:px-4 py-2 sm:py-3 text-gray-900 font-medium text-xs sm:text-sm">{member.name}</td>
                  <td className="text-center px-2 sm:px-4 py-2 sm:py-3">
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                      {member.submitted}
                    </span>
                  </td>
                  <td className="text-center px-2 sm:px-4 py-2 sm:py-3">
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                      {member.draft}
                    </span>
                  </td>
                  <td className="text-center px-2 sm:px-4 py-2 sm:py-3">
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium">
                      {member.missing}
                    </span>
                  </td>
                  <td className="text-right px-2 sm:px-4 py-2 sm:py-3">
                    <div className="flex items-center justify-end gap-1 sm:gap-2">
                      <div className="w-16 sm:w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(parseFloat(member.submissionRate), 100)}%` }}
                        ></div>
                      </div>
                      <span className="font-semibold text-gray-900 w-10 text-right text-xs sm:text-sm">
                        {member.submissionRate}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Period Summary</h4>
          <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">From:</span>
              <span className="font-medium text-gray-900">{reportData.period.from}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">To:</span>
              <span className="font-medium text-gray-900">{reportData.period.to}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Days:</span>
              <span className="font-medium text-gray-900">{reportData.period.daysInRange}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Members:</span>
              <span className="font-medium text-gray-900">{stats.teamMemberCount}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h4 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Quick Stats</h4>
          <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Expected:</span>
              <span className="font-medium text-gray-900">{stats.totalExpectedReports}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Completed:</span>
              <span className="font-medium text-green-600">{stats.submittedReports}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Incomplete:</span>
              <span className="font-medium text-yellow-600">{stats.draftReports}</span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="font-semibold text-gray-900">Overall:</span>
              <span className="font-bold text-blue-600">{stats.submissionRate}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Metric Card Component
 */
const MetricCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200'
  };

  return (
    <div className={`border rounded-lg p-4 ${colorClasses[color]}`}>
      <div className="text-3xl mb-2">{icon}</div>
      <p className="text-sm font-medium opacity-75">{title}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
};

export default TeamAnalyticsPanel;
